import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
// Materials
import { Box } from "@mui/material";
// Redux
import { actions } from "@/redux/reducers/User";
import { RootState } from "@/redux/store";
// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
// API
import { errorLogger } from "@/Common/helpers/logger";
// Icons
import PersonCircleIcon from "@/User/assets/icons/person-circle.svg";
// Components
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import ProfilePictureDialogComponent from "./ProfilePictureDialogComponent";
// Styles
import "@/User/assets/styles/UserProfile.css";
// Interfaces and Types
import { IUser } from "@/Common/interfaces/user";
import UserProfileMainInformation from "./UserProfileMainInformation";
import UserProfileError from "./UserProfileError";
import UserProfileContainerDetails from "./UserProfileContainerDetails";
import { resolveuserProfileSubscriptionPipeline } from "@/User/subscriptions/pipelines";

type TUpdateUserResponse = {
	data: {
		onUpdateUser: Pick<IUser, "_id" | "employeeId" | "username" | "communications" | "supervisor">;
	};
};

export default function UserProfileContainer() {
	const { user } = useSelector((state: RootState) => state.user);
	const { location } = useSelector((state: RootState) => state.location);

	const dispatch = useDispatch();
	const { getSubscription, addSubscription, clearSubscriptionContext } = useSubscriptions();

	const [hov, setHov] = useState<boolean>(false);
	const [onCloseCallBack, setOnCloseCallBack] = useState<boolean>(false);
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [updating, setUpdating] = useState<boolean>(false);

	const userProfileSubscription = getSubscription(["userProfile", user?._id]);

	userProfileSubscription?.subscriptions?.onUpdateUser?.setCallback((response: TUpdateUserResponse) => {
		const updatedUser = response.data?.onUpdateUser;
		if (updatedUser) {
			try {
				const newUser: IUser = { ...(user as IUser) };
				newUser["username"] = updatedUser.username;
				newUser["communications"] = updatedUser.communications;
				newUser["supervisor"] = updatedUser.supervisor;
				dispatch(actions.SET_USER({ user: newUser }));
			} catch (error) {
				errorLogger(error as Error);
			}

			setUpdating(false);
		}
	});

	const handleClick = () => {
		setOpenDialog(true);
		setOnCloseCallBack(false);
	};

	const setUpdatingCallBack = (value: boolean) => {
		return setUpdating(value);
	};

	const handleProfilePictureOnClose = () => {
		setOpenDialog(false);
		setOnCloseCallBack(true);
	};

	const handleHov = (status: boolean) => {
		setHov(status);
	};

	useEffect(() => {
		if (location && user?._id) {
			addSubscription(["userProfile", user._id], [resolveuserProfileSubscriptionPipeline(user._id)]);
		}

		return () => {
			setUpdating(false);
			clearSubscriptionContext();
		};
	}, [addSubscription, location, user?._id, clearSubscriptionContext]);

	return (
		<BodyLayer>
			<RoundedBox className="min-h-[630px]">
				<Box>
					<ProfilePictureDialogComponent
						open={openDialog}
						onCloseDialog={handleProfilePictureOnClose}
						onCloseCallBack={onCloseCallBack}
						imageSrc={user?.profilePicture ?? PersonCircleIcon}
						setUpdatingCallBack={setUpdatingCallBack}
					/>
					<UserProfileError error={user?.userError} />

					<UserProfileMainInformation
						fullName={user?.fullName}
						position={user?.position}
						isLoading={updating}
						hov={hov}
						profilePicture={user?.profilePicture}
						handleClick={handleClick}
						handleHov={handleHov}
					/>

					<UserProfileContainerDetails isLoading={updating} user={user} />
				</Box>
			</RoundedBox>
		</BodyLayer>
	);
}
